import { useContext } from 'react';
import { TelemetryContext } from './telemetryProvider';
import { useUser } from '../../contexts/UserContext';
import {
  addDatadogAction,
  addDatadogError,
  addDatadogLog,
  normalizeErrorForTelemetry,
} from './datadog';
import {
  safeTelemetryPreferenceEntries,
  sanitizeTelemetryProperties,
} from './telemetryPreferences';
import { LANGUAGE_STORAGE_KEY } from '../../i18n/languageStorage';

export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
}

export function useLogger() {
  const telemetryContext = useContext(TelemetryContext);
  if (telemetryContext === undefined) {
    throw new Error('useLogger must be used within a TelemetryProvider');
  }

  const { appId, userPreferences } = useUser();

  const language = localStorage.getItem(LANGUAGE_STORAGE_KEY) ?? 'en';

  const isDev = import.meta.env.DEV;

  // App Insights is first-party and its payloads MUST stay exactly as they were
  // before Datadog was introduced: full `userPreferences` flattened, raw
  // properties, raw errors. All PII/token hygiene applies ONLY to the Datadog
  // forward (third-party sink), never to the App Insights write below.
  const baseProperties = {
    'user.siteId': appId,
    'user.language': language,
    ...Object.entries(userPreferences).reduce(
      (acc, [key, value]) => {
        acc[`user.${key}`] = value;
        return acc;
      },
      {} as Record<string, any>,
    ),
  };

  // Datadog-only base: user preferences filtered to allowlisted scalars so an
  // arbitrary/PII/token/free-text preference can never be stamped on every
  // Datadog event. App Insights (baseProperties above) is unaffected.
  const datadogBaseProperties = {
    'user.siteId': appId,
    'user.language': language,
    ...safeTelemetryPreferenceEntries(userPreferences).reduce(
      (acc, [key, value]) => {
        acc[`user.${key}`] = value;
        return acc;
      },
      {} as Record<string, any>,
    ),
  };

  // App Insights payload — original shape, untouched.
  const buildAiProps = (properties?: Record<string, any>) => ({
    ...baseProperties,
    ...properties,
  });
  // Datadog payload — allowlisted base + sanitized (deny-listed keys dropped,
  // email/JWT/Bearer patterns redacted, non-scalars removed) properties.
  const buildDatadogProps = (properties?: Record<string, any>) => ({
    ...datadogBaseProperties,
    ...sanitizeTelemetryProperties(properties),
  });

  // Dual-write helpers: every existing telemetry call continues to flow into
  // Application Insights unchanged, and we mirror a scrubbed copy into Datadog.
  // Datadog calls are safe no-ops when DD isn't initialized, so this is risk-free.
  const telemetry = {
    ...telemetryContext,
    trackEventWithContext: (name: string, properties?: Record<string, any>) => {
      try {
        telemetryContext.trackEvent(name, buildAiProps(properties));
      } catch (error) {
        console.warn(`Failed to track event '${name}':`, error);
      }
      try {
        addDatadogAction(name, buildDatadogProps(properties));
      } catch (error) {
        console.warn(`Failed to forward event '${name}' to Datadog:`, error);
      }
    },
    trackPageViewWithContext: (name?: string, properties?: Record<string, any>) => {
      try {
        telemetryContext.trackPageView(name, buildAiProps(properties));
      } catch (error) {
        console.warn(`Failed to track page view '${name || 'unnamed'}':`, error);
      }
      // Datadog RUM views are driven exclusively from AppRoutes' useLocation
      // effect (see App.tsx) so dashboards group by canonical route names and
      // every navigation produces exactly one view. Calling startDatadogView
      // here would create a second view (different name, ~1ms apart) and
      // defeat trackViewsManually=true in datadog.ts.
    },
    trackExceptionWithContext: (error: Error, properties?: Record<string, any>) => {
      try {
        // App Insights is first-party — it gets the full Error + full properties.
        telemetryContext.trackException(error, buildAiProps(properties));
      } catch (trackingError) {
        console.warn('Failed to track exception:', trackingError);
      }
      try {
        // Datadog is a third-party sink: never hand it the raw Error. Its
        // message/stack can embed URLs or identifiers (and MSAL-style errors
        // carry account/login hints). Forward a scrubbed error whose message is
        // the caller's static log message (developer-authored) — the error
        // `name` + `errorCode` are preserved for grouping.
        const datadogProps: Record<string, unknown> = buildDatadogProps(properties);
        const genericMessage =
          typeof datadogProps.message === 'string' && datadogProps.message
            ? datadogProps.message
            : 'Unhandled error';
        const { error: safeError, context } = normalizeErrorForTelemetry(error, { genericMessage });
        addDatadogError(safeError, { ...datadogProps, ...context });
      } catch (trackingError) {
        console.warn('Failed to forward exception to Datadog:', trackingError);
      }
    },
    trackTraceWithContext: (
      message: string,
      properties?: Record<string, any>,
      level: 'debug' | 'info' | 'warn' | 'error' = 'info',
    ) => {
      try {
        telemetryContext.trackTrace(message, buildAiProps(properties));
      } catch (error) {
        console.warn(`Failed to track trace '${message}':`, error);
      }
      try {
        addDatadogLog(level, message, buildDatadogProps(properties));
      } catch (error) {
        console.warn(`Failed to forward trace '${message}' to Datadog:`, error);
      }
    },
  };

  return {
    debug: (message: string, data?: any): void => {
      if (isDev) {
        console.debug(message, data);
      }
    },

    info: (message: string, data?: any): void => {
      if (isDev) {
        console.info(message, data);
      }
      telemetry.trackTraceWithContext(message, data, 'info');
    },

    warn: (message: string, data?: any): void => {
      if (isDev) {
        console.warn(message, data);
      }
      // Spread data FIRST so the explicit logger-controlled `level` always
      // wins. With the old `{ level: ..., ...data }` order, a caller passing
      // `data.level` would silently overwrite the level we tag here.
      telemetry.trackTraceWithContext(message, { ...data, level: LogLevel.WARN }, 'warn');
    },

    error: (message: string, error?: Error, data?: any): void => {
      if (isDev) {
        console.error(message, error, data);
      }

      // Same precedence rule as warn(): explicit logger params (`message`
      // and `level`) override any same-named keys in `data`. Otherwise
      // `logger.error('Login failed', err, { message: 'detail' })` would
      // lose 'Login failed' from the dashboard properties.
      if (error) {
        telemetry.trackExceptionWithContext(error, { ...data, message });
      } else {
        telemetry.trackTraceWithContext(message, { ...data, level: LogLevel.ERROR }, 'error');
      }
    },

    event: (name: string, properties?: Record<string, any>): void => {
      telemetry.trackEventWithContext(name, properties);
    },

    pageView: (name?: string, properties?: Record<string, any>): void => {
      telemetry.trackPageViewWithContext(name, properties);
    },

    telemetry,
  };
}
